import * as React from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
// Logo padrão (SmartTower), para quando a empresa não tiver logo cadastrada.
import LogoSmartTower from "../assets/logo-smarttower.png";
import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import { useTheme } from "@mui/material/styles";
import { Outlet } from "react-router-dom";
import AccountMenu from "../components/buttomRole";
import { useModule } from "../hooks/module";
import { useThemeMode } from "../themes/modeTheme";
import { Link } from "react-router-dom";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { useLocationHistoryStack } from "../hooks/locationHistoryStack";
import { RealTImeHUBProvider } from "../hooks/realTimeHub";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Collapse, Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";
import DehazeIcon from "@mui/icons-material/Dehaze";
import ModuleMenu from "../components/buttomModule";
import { BreadcrumbsCustom } from "../components/Breadcrumbs";
import { BreadcrumbsContentProvider } from "@/hooks/breadcrumbsContent";
import { DrawerProvider } from "./DrawerContext";

const openedMixin = (theme: Theme): CSSObject => ({
  width: "var(--drawer-width)",
  maxHeight: "98.6vh",
  borderRadius: theme.shape.borderRadius,
  margin: 6,
  // backgroundColor: "red", // Removed conflict
  borderRight: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  maxHeight: "98.6vh",
  borderRadius: theme.shape.borderRadius,
  margin: 5,
  // background: theme.palette.primary.light, // Removed conflict
  borderRight: `1px solid ${theme.palette.divider}`,
  width: `var(--drawer-mini-width)`,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: theme.palette.primary[theme.palette.mode],
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: "var(--drawer-width)",
  flexShrink: 0,
  // background: theme.palette.primary[theme.palette.mode], / Removed conflict
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function Layout() {
  const [open, setOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState({});
  const { routes, changeModule, modules } = useModule();
  const { toggleTheme, mode, logos } = useThemeMode();
  const theme = useTheme();
  const { current } = useLocationHistoryStack();
  const drawerWidthOpen = 270;
  const drawerWidthClosed = 60;
  const handleDrawer = () => {
    setOpen((o) => !o);
  };
  const [activeMenu, setActiveMenu] = React.useState("main");

  React.useEffect(() => {
    if (!open && activeMenu === "submenu") {
      setActiveMenu("main");
    }
  }, [open, activeMenu]);

  // Expand parent/child menus automatically when navigating directly to sub-routes
  React.useEffect(() => {
    if (!current?.pathname) return;
    let changed = false;
    const newExpanded = { ...expanded };

    routes.forEach((route, index) => {
      if (route.children) {
        // Check if any child is active
        const hasActiveChild = route.children.some((child) => {
          if (child.path === current.pathname) return true;
          if (child.children) {
            return child.children.some(
              (grandChild) => grandChild.path === current.pathname,
            );
          }
          return false;
        });

        if (hasActiveChild && !expanded[index]) {
          newExpanded[index] = true;
          changed = true;
        }

        // Check if any grandchild is active
        route.children.forEach((child) => {
          if (child.children) {
            const hasActiveGrandChild = child.children.some(
              (grandChild) => grandChild.path === current.pathname,
            );
            const childIndex = `${index}-${child.path}`;
            if (hasActiveGrandChild && !expanded[childIndex]) {
              newExpanded[childIndex] = true;
              changed = true;
            }
          }
        });
      }
    });

    if (changed) {
      setExpanded(newExpanded);
    }
  }, [current?.pathname, routes]);

  return (
    <BreadcrumbsContentProvider>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          background:
            theme.palette.mode === "dark"
              ? theme.palette.primary.main
              : theme.palette.secondary.main,
        }}
      >
        <RealTImeHUBProvider>
          <Drawer
            variant="permanent"
            open={open}
            elevation={0}
            sx={{
              width: open ? drawerWidthOpen : drawerWidthClosed,
              flexShrink: 0,
              whiteSpace: "nowrap",
              height: "100vh",
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              ".MuiDrawer-paper": {
                width: open ? drawerWidthOpen : drawerWidthClosed,
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                borderRight: "none",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main, // Enforce correct color
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "none"
                    : "4px 0 24px rgba(0, 0, 0, 0.02)",
                "&::-webkit-scrollbar": {
                  width: "4px",
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[800]
                      : "#E0E0E0",
                  borderRadius: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
              },
            }}
          >
            <DrawerHeader
              sx={{ minHeight: "var(--drawer-mini-width) !important" }}
            >
              <IconButton
                disableRipple
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawer}
                edge="start"
                sx={{
                  padding: open ? 0 : "12px",
                  paddingTop: open ? 1 : "12px",
                  margin: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  minWidth: open ? "auto" : "56px",
                  minHeight: open ? "auto" : "56px",
                  width: open ? "auto" : "100%",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    height: "30px",
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: "4px",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Tooltip title={"Menu"} arrow placement="right">
                      <Box sx={{ display: "flex" }}>
                        <DehazeIcon
                          sx={{
                            fontSize: open ? "20px" : "20px",
                            color: theme.palette.text.secondary,
                            marginRight: open ? "220px" : "-15px",
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </Box>

                  {/* Botão de tema - apenas no modo aberto */}
                  {open && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTheme();
                      }}
                      sx={{
                        color: theme.palette.text.secondary,
                        position: "absolute",
                        right: 8,
                        top: 8,
                        fontSize: "1.2rem",
                        zIndex: 1001,
                        backgroundColor: "transparent",
                        borderRadius: "4px",
                        padding: "4px",
                        transition: "transform 0.5s ease",
                        transform:
                          mode === "dark" ? "rotate(180deg)" : "rotate(0deg)",
                        "&:hover": {
                          backgroundColor: "transparent",
                          color: theme.palette.text.primary,
                        },
                      }}
                    >
                      {mode === "light" ? (
                        <DarkModeOutlinedIcon fontSize="inherit" />
                      ) : (
                        <WbSunnyOutlinedIcon fontSize="inherit" />
                      )}
                    </IconButton>
                  )}
                </Box>
                {open && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100%",
                      marginTop: "4px",
                    }}
                  >
                    <Link
                      to={"/towermanagement"}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textDecoration: "none",
                        gap: "6px",
                      }}
                    >
                      {!logos.loading && (
                        <Box
                          component="img"
                          src={(mode === "dark" ? logos.dark : logos.light) ?? LogoSmartTower}
                          alt="Logo da empresa"
                          sx={{
                            width: "170px",
                            maxHeight: "72px",
                            objectFit: "contain",
                            // A wordmark SmartTower é clara; no modo claro precisa escurecer.
                            filter:
                              mode === "light" && !logos.light
                                ? "brightness(0.5) saturate(3.5) contrast(1.9) hue-rotate(25deg)"
                                : "none",
                          }}
                        />
                      )}
                    </Link>
                  </Box>
                )}
              </IconButton>
            </DrawerHeader>

            <List
              sx={{
                flex: 1,
                overflowY: "auto",
                scrollbarWidth: "none",
                padding: open ? 2 : 0,
                marginTop: open ? 1 : -1,
                color: theme.palette.primary.contrastText,
                display: "flex",
                gap: 1.5,
                flexDirection: "column",
              }}
            >
              <Collapse in={activeMenu === "main"} unmountOnExit mountOnEnter>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <ListItem disablePadding sx={{ display: "block" }}>
                    {open && (
                      <Tooltip title={"Módulos"} arrow placement="right">
                        <Box>
                          <ListItemButton
                            onClick={() => {
                              if (open) setActiveMenu("submenu");
                            }}
                            sx={{
                              minHeight: 36,
                              padding: "4px 16px",
                              mb: 1,
                              borderRadius: "8px",
                              "&:hover": {
                                background: theme.palette.action.hover,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                paddingLeft: open ? "0px" : "0px",
                                mr: 2,
                                color: theme.palette.text.secondary,
                                opacity: open ? 1 : 0,
                                visibility: open ? "visible" : "hidden",
                                transition: "all 0.3s ease",
                              }}
                            >
                              <ArrowCircleLeftOutlinedIcon
                                sx={{ color: "#757575", fontSize: "1.2rem" }}
                              />
                            </ListItemIcon>

                            <ListItemText
                              primary="Módulos"
                              primaryTypographyProps={{
                                sx: {
                                  color: theme.palette.text.secondary,
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                },
                              }}
                            />
                          </ListItemButton>
                        </Box>
                      </Tooltip>
                    )}
                  </ListItem>
                  {open && (
                    <Divider
                      sx={{
                        my: 0.5,
                        mx: 2,
                        borderColor: theme.palette.divider,
                      }}
                    />
                  )}
                  {routes.map(({ path, name, icon: Icon, children }, index) => {
                    const isParentSelected = path === current?.pathname;

                    return (
                      <ListItem
                        key={index}
                        disablePadding
                        sx={{ display: "block" }}
                      >
                        {!children && (
                          <Tooltip title={name} arrow placement="right">
                            <Box>
                              <ListItemButton
                                component={Link}
                                to={path}
                                sx={{
                                  background: "transparent",
                                  color:
                                    path === current?.pathname
                                      ? theme.palette.accentText
                                      : theme.palette.text.primary,
                                  opacity: path === current?.pathname ? 1 : 0.7,
                                  borderRadius: "0px",
                                  padding: "10px 0",
                                  minHeight: 48,
                                  justifyContent: open ? "initial" : "center",
                                  px: 3,
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    background: theme.palette.action.hover,
                                    color: theme.palette.text.primary,
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: open ? 2 : "auto",
                                    justifyContent: "center",
                                    color:
                                      path === current?.pathname
                                        ? theme.palette.accentText
                                        : "inherit",
                                  }}
                                >
                                  <Icon
                                    sx={{
                                      color: "inherit",
                                      fontSize: "1.25rem",
                                    }}
                                  />
                                </ListItemIcon>
                                {open && (
                                  <ListItemText
                                    primary={name}
                                    sx={{
                                      opacity: open ? 1 : 0,
                                    }}
                                    primaryTypographyProps={{
                                      sx: {
                                        fontWeight:
                                          path === current?.pathname
                                            ? 600
                                            : 400,
                                        color: "inherit",
                                        fontSize: "0.95rem",
                                      },
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            </Box>
                          </Tooltip>
                        )}
                        {children && (
                          <>
                            <Tooltip title={name} arrow placement="right">
                              <Box>
                                <ListItemButton
                                  {...(path === "/towermanagement/activities"
                                    ? { component: Link, to: path }
                                    : {})}
                                  onClick={() =>
                                    setExpanded((curr) => ({
                                      ...curr,
                                      [index]: !curr[index],
                                    }))
                                  }
                                  sx={{
                                    background: "transparent",
                                    color: isParentSelected
                                      ? theme.palette.accentText
                                      : theme.palette.text.primary,
                                    opacity: isParentSelected ? 1 : 0.7,
                                    borderRadius: "0px",
                                    padding: "10px 0",
                                    minHeight: 48,
                                    justifyContent: open ? "initial" : "center",
                                    px: 3,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      background: theme.palette.action.hover,
                                      color: theme.palette.text.primary,
                                    },
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{
                                      minWidth: 0,
                                      mr: open ? 2 : "auto",
                                      justifyContent: "center",
                                      color: isParentSelected
                                        ? theme.palette.accentText
                                        : "inherit",
                                    }}
                                  >
                                    <Icon
                                      sx={{
                                        color: "inherit",
                                        fontSize: "1.25rem",
                                      }}
                                    />
                                  </ListItemIcon>

                                  {open && (
                                    <ListItemText
                                      primary={name}
                                      sx={{
                                        opacity: open ? 1 : 0,
                                      }}
                                      primaryTypographyProps={{
                                        sx: {
                                          fontWeight: isParentSelected
                                            ? 600
                                            : 400,
                                          color: isParentSelected
                                            ? theme.palette.accentText
                                            : theme.palette.text.secondary,
                                          fontSize: "0.95rem",
                                        },
                                      }}
                                    />
                                  )}

                                  {/* caret alinhado à direita */}
                                  {open && (
                                    <KeyboardArrowRightIcon
                                      sx={{
                                        ml: "auto",
                                        color: theme.palette.text.secondary,
                                        transform: expanded[index]
                                          ? "rotate(90deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.3s ease",
                                      }}
                                    />
                                  )}
                                </ListItemButton>
                              </Box>
                            </Tooltip>

                            <Collapse
                              in={expanded[index]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <List component="div" disablePadding>
                                {children.map((child) => {
                                  const childSelected =
                                    child.path === current?.pathname ||
                                    (child.children &&
                                      child.children.some(
                                        (gc) => gc.path === current?.pathname,
                                      ));

                                  // Se o child tem filhos próprios, renderizar como grupo aninhado
                                  if (child.children?.length) {
                                    const childIndex = `${index}-${child.path}`;
                                    return (
                                      <React.Fragment key={child.path}>
                                        <ListItemButton
                                          component={Link}
                                          to={child.path}
                                          onClick={() =>
                                            setExpanded((curr) => ({
                                              ...curr,
                                              [childIndex]: !curr[childIndex],
                                            }))
                                          }
                                          sx={{
                                            minHeight: 42,
                                            justifyContent: open
                                              ? "initial"
                                              : "center",
                                            px: 3,
                                            pl: open ? 5 : 3,
                                            background: "transparent",
                                            color: childSelected
                                              ? theme.palette.accentText
                                              : theme.palette.text.primary,
                                            opacity: childSelected ? 1 : 0.7,
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              background:
                                                theme.palette.action.hover,
                                              color: theme.palette.text.primary,
                                            },
                                          }}
                                        >
                                          <ListItemIcon
                                            sx={{
                                              minWidth: 0,
                                              mr: open ? 2 : "auto",
                                              justifyContent: "center",
                                              color: childSelected
                                                ? theme.palette.accentText
                                                : "inherit",
                                            }}
                                          >
                                            <child.icon
                                              sx={{
                                                color: "inherit",
                                                fontSize: "1.1rem",
                                              }}
                                            />
                                          </ListItemIcon>

                                          {open && (
                                            <ListItemText
                                              primary={child.name}
                                              sx={{
                                                opacity: open ? 1 : 0,
                                                pr: 2.5,
                                              }}
                                              primaryTypographyProps={{
                                                sx: {
                                                  fontWeight: childSelected
                                                    ? 600
                                                    : 400,
                                                  color: "inherit",
                                                  fontSize: "0.9rem",
                                                },
                                              }}
                                            />
                                          )}

                                          {open && (
                                            <KeyboardArrowRightIcon
                                              sx={{
                                                ml: "auto",
                                                color:
                                                  theme.palette.text.secondary,
                                                transform: expanded[childIndex]
                                                  ? "rotate(90deg)"
                                                  : "rotate(0deg)",
                                                transition:
                                                  "transform 0.3s ease",
                                              }}
                                            />
                                          )}
                                        </ListItemButton>
                                        <Collapse
                                          in={expanded[childIndex]}
                                          timeout="auto"
                                          unmountOnExit
                                        >
                                          <List component="div" disablePadding>
                                            {child.children.map(
                                              (grandChild) => {
                                                const grandChildSelected =
                                                  grandChild.path ===
                                                  current?.pathname;
                                                return (
                                                  <ListItemButton
                                                    key={grandChild.path}
                                                    component={Link}
                                                    to={grandChild.path}
                                                    sx={{
                                                      minHeight: 40,
                                                      justifyContent: open
                                                        ? "initial"
                                                        : "center",
                                                      px: 3,
                                                      pl: open ? 9 : 3,
                                                      background: "transparent",
                                                      color: grandChildSelected
                                                        ? theme.palette.accentText
                                                        : theme.palette.text
                                                            .primary,
                                                      opacity:
                                                        grandChildSelected
                                                          ? 1
                                                          : 0.7,
                                                      transition:
                                                        "all 0.2s ease",
                                                      "&:hover": {
                                                        background:
                                                          theme.palette.action
                                                            .hover,
                                                        color:
                                                          theme.palette.text
                                                            .primary,
                                                      },
                                                    }}
                                                  >
                                                    <ListItemIcon
                                                      sx={{
                                                        minWidth: 0,
                                                        mr: open ? 2 : "auto",
                                                        justifyContent:
                                                          "center",
                                                        color:
                                                          grandChildSelected
                                                            ? theme.palette.accentText
                                                            : "inherit",
                                                      }}
                                                    >
                                                      <grandChild.icon
                                                        sx={{
                                                          color: "inherit",
                                                          fontSize: "1rem",
                                                        }}
                                                      />
                                                    </ListItemIcon>

                                                    {open && (
                                                      <ListItemText
                                                        primary={
                                                          grandChild.name
                                                        }
                                                        sx={{
                                                          opacity: open ? 1 : 0,
                                                        }}
                                                        primaryTypographyProps={{
                                                          sx: {
                                                            fontWeight:
                                                              grandChildSelected
                                                                ? 600
                                                                : 400,
                                                            color: "inherit",
                                                            fontSize: "0.85rem",
                                                          },
                                                        }}
                                                      />
                                                    )}
                                                  </ListItemButton>
                                                );
                                              },
                                            )}
                                          </List>
                                        </Collapse>
                                      </React.Fragment>
                                    );
                                  }

                                  // Caso contrário, renderizar como item folha normal
                                  return (
                                    <Tooltip
                                      key={child.path}
                                      title={child.name}
                                      arrow
                                      placement="right"
                                    >
                                      <Box>
                                        <ListItemButton
                                          component={Link}
                                          to={child.path}
                                          sx={{
                                            minHeight: 42,
                                            justifyContent: open
                                              ? "initial"
                                              : "center",
                                            px: 3,
                                            pl: open ? 6 : 3,
                                            background: "transparent",
                                            color: childSelected
                                              ? theme.palette.accentText
                                              : theme.palette.text.primary,
                                            opacity: childSelected ? 1 : 0.7,
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              background:
                                                theme.palette.action.hover,
                                              color: theme.palette.text.primary,
                                            },
                                          }}
                                        >
                                          <ListItemIcon
                                            sx={{
                                              minWidth: 0,
                                              mr: open ? 2 : "auto",
                                              justifyContent: "center",
                                              color: childSelected
                                                ? theme.palette.accentText
                                                : "inherit",
                                            }}
                                          >
                                            <child.icon
                                              sx={{
                                                color: "inherit",
                                                fontSize: "1.1rem",
                                              }}
                                            />
                                          </ListItemIcon>

                                          {open && (
                                            <ListItemText
                                              primary={child.name}
                                              sx={{ opacity: open ? 1 : 0 }}
                                              primaryTypographyProps={{
                                                sx: {
                                                  fontWeight: childSelected
                                                    ? 600
                                                    : 400,
                                                  color: "inherit",
                                                  fontSize: "0.9rem",
                                                },
                                              }}
                                            />
                                          )}
                                        </ListItemButton>
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </List>
                            </Collapse>
                          </>
                        )}
                      </ListItem>
                    );
                  })}
                </Box>
              </Collapse>

              <Collapse
                in={activeMenu === "submenu" && open}
                unmountOnExit
                mountOnEnter
              >
                <Box>
                  <ModuleMenu
                    open={open}
                    changeModule={(data) => {
                      changeModule(data);
                      setActiveMenu("main");
                    }}
                    modules={modules}
                  />
                </Box>
              </Collapse>
              <ListItem disablePadding sx={{ display: "block" }}></ListItem>
            </List>
          </Drawer>
          <Box
            component="main"
            className="main-layout"
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              borderRadius: 1,
              height: "100vh",
              background:
                theme.palette.mode === "dark"
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
              borderCollapse: "collapse",
              "& td, & th": {
                border: "none",
              },
            }}
          >
            <Box
              sx={{
                position: "sticky",

                zIndex: 1200,
                borderRadius: 1,
                mx: "10px",
                mt: "10px",
                px: 2,
                py: 0.5,
                boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                backdropFilter: "blur(8px)",
                background: `linear-gradient(135deg, ${
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }f0, ${
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }ff)`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: "38px",
                  maxHeight: "38px",
                  width: "100%",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <BreadcrumbsCustom />
                </Box>
                <AccountMenu variant="header" />
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                padding: "10px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <DrawerProvider open={open}>
                <Outlet />
              </DrawerProvider>
            </Box>
          </Box>
        </RealTImeHUBProvider>
      </Box>
    </BreadcrumbsContentProvider>
  );
}
